import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import Button from '@material-ui/core/Button';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {AccordionSummary, Typography, AccordionDetails, Accordion, Grid} from '@material-ui/core';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import './ExternalEnrollmentFileUploadDetails.sass';
import {getEnrollmentApplicationsDetail} from 'Enrollment/ducks/enrollmentFileProcessing';
import {ReducerState} from 'rootReducer';
import {useParams, useHistory} from 'react-router-dom';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {ExternalEnrollmentFileUploadSteps} from 'Shared/constants/ExternalEnrollmentFileUploadSteps';
import {EmployeeErrorDetail} from 'Enrollment/models/EmployeeErrorDetail';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import BrokerSpreadsheetFileSummaryAccordion from './BrokerSpreadsheetFileSummaryAccordion';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {EnrollmentFileStatus} from 'Enrollment/models/EnrollmentFilesNotProcessed';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import HorizontalStatusTracker from './HorizontalStatusTracker/HorizontalStatusTracker';
import {EnrollmentFilesDetailDto} from 'Enrollment/api';
import CancelIcon from '@material-ui/icons/Cancel';
import {ExportToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';

const steps = [
  {id: 0, value: 'File Uploaded'},
  {id: 1, value: 'Received'},
  {id: 2, value: 'Processed'},
  {id: 3, value: 'Confirmed'},
];

const ExternalEnrollmentFileUploadDetails = () => {
  const {enrollmentApplicationsDetails} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  const history = useHistory();
  const dispatch = useDispatch();
  const {fileId} = useParams();
  const isFileUploadBefore = (enrollmentFile1: EnrollmentApplication, enrollmentFile2: EnrollmentApplication) =>
    new Date(enrollmentFile2.fileUploadedDate!).getTime() - new Date(enrollmentFile1.fileUploadedDate!).getTime();

  useEffect(() => {
    dispatch(getEnrollmentApplicationsDetail(fileId!));
  }, [dispatch, fileId]);

  const getActiveStepperClass = (
    invalidRecordCount: number,
    rowCount: number,
    status: EnrollmentFileStatus,
    index: number,
    validRecordCount: number
  ) => {
    if (index === 0) {
      if (rowCount === validRecordCount) return 'stepper__completed';
      else if (rowCount === invalidRecordCount) return 'stepper__error';
      else if (validRecordCount > 0 && invalidRecordCount > 0) return 'stepper__warning';
      else return 'stepper__inactive';
    } else if (status === EnrollmentFileStatusType.RECEIVED) {
      if (invalidRecordCount > 0 || rowCount === 0) return 'stepper__error';
      else if (rowCount === validRecordCount) return 'stepper__completed';
      else if (validRecordCount > 0 && invalidRecordCount > 0) return 'stepper__warning';
      else return 'stepper__inactive';
    } else if (status === EnrollmentFileStatusType.PROCESSING) {
      return 'stepper__active';
    } else if (status === EnrollmentFileStatusType.COMPLETED) {
      return 'stepper__completed';
    } else if (status === EnrollmentFileStatusType.CANCELED) {
      return 'stepper__canceled';
    }
    return 'stepper__inactive';
  };

  const getCompletedStatusClass = (
    invalidRecordCount: number,
    rowCount: number,
    status: EnrollmentFileStatus,
    index: number,
    validRecordCount: number
  ) => {
    if (index === 0) {
      if (rowCount === validRecordCount) return 'stepper__completed';
      else if (rowCount === invalidRecordCount) return 'stepper__error';
      else if (validRecordCount > 0 && invalidRecordCount > 0) return 'stepper__warning';
      else return 'stepper__inactive';
    } else {
      return 'stepper__completed';
    }
  };

  const getActiveIcon = (
    index: number,
    validRecordCount: number,
    invalidRecordCount: number,
    rowCount: number,
    status: EnrollmentFileStatus,
    fileStatusIdBeforeCanceled: number
  ) => {
    const isCanceledReceived =
      status === EnrollmentFileStatusType.CANCELED && fileStatusIdBeforeCanceled === EnrollmentFileStatusType.RECEIVED;
    const isCanceledProcessing =
      status === EnrollmentFileStatusType.CANCELED &&
      fileStatusIdBeforeCanceled === EnrollmentFileStatusType.PROCESSING;

    const isReturnedReceived =
      status === EnrollmentFileStatusType.Returned && fileStatusIdBeforeCanceled === EnrollmentFileStatusType.RECEIVED;
    const isReturnedProcessing =
      status === EnrollmentFileStatusType.Returned &&
      fileStatusIdBeforeCanceled === EnrollmentFileStatusType.PROCESSING;

    if (index === ExternalEnrollmentFileUploadSteps.FileUpload) {
      if (rowCount === validRecordCount) return 'fas fa-check-circle status__successful';
      else if (rowCount === invalidRecordCount) return 'fas fa-times-circle status__unsuccessful';
      else if (validRecordCount > 0 && invalidRecordCount > 0) return 'fas fa-exclamation-triangle status__warning';
      else return 'far fa-circle status__inactive';
    } else if (index === ExternalEnrollmentFileUploadSteps.Received) {
      if (isCanceledReceived) return 'fas fa-times-circle status__canceled';
      else if (isReturnedReceived && invalidRecordCount > 0) return 'fas fa-times-circle status__unsuccessful';
      else if (isReturnedReceived && rowCount === validRecordCount) return 'fas fa-times-circle status__unsuccessful';
      else if (invalidRecordCount > 0) return 'fas fa-times-circle status__unsuccessful';
      else if (rowCount === validRecordCount) return 'fas fa-check-circle status__successful';
      else return 'far fa-circle status__inactive';
    } else if (index === ExternalEnrollmentFileUploadSteps.Processing) {
      if (isCanceledProcessing) return 'fas fa-times-circle status__canceled';
      else if (isReturnedProcessing) return 'fas fa-times-circle status__unsuccessful';
      else if (rowCount === validRecordCount && status === EnrollmentFileStatusType.COMPLETED)
        return 'fas fa-check-circle status__successful';
      else if (status === EnrollmentFileStatusType.PROCESSING) return 'far fa-circle status__processing';
      else return 'far fa-circle status__inactive';
    } else if (index === ExternalEnrollmentFileUploadSteps.ProcessingComplete) {
      if (status === EnrollmentFileStatusType.PROCESSING) return 'far fa-circle status__inactive';
      else if (status === EnrollmentFileStatusType.COMPLETED) return 'fas fa-check-circle status__successful';
      else return 'far fa-circle status__inactive';
    } else {
      return 'stepper__inactive';
    }
  };

  const getActiveSteps = (id: number, fileStatusTypeBeforeCanceled: number) => {
    switch (id) {
      case EnrollmentFileStatusType.RECEIVED:
        return ExternalEnrollmentFileUploadSteps.Received;
      case EnrollmentFileStatusType.PROCESSING:
        return ExternalEnrollmentFileUploadSteps.Processing;
      case EnrollmentFileStatusType.COMPLETED:
        return ExternalEnrollmentFileUploadSteps.Completed;
      case EnrollmentFileStatusType.CANCELED:
      case EnrollmentFileStatusType.Returned:
        return getCanceledSteps(fileStatusTypeBeforeCanceled);
      default:
        return 0;
    }
  };

  const getProcessingSteps = (id: number) => {
    switch (id) {
      case EnrollmentFileStatusType.PROCESSING:
        return ExternalEnrollmentFileUploadSteps.Processing;
      case EnrollmentFileStatusType.COMPLETED:
        return ExternalEnrollmentFileUploadSteps.Completed;
      case EnrollmentFileStatusType.RECEIVED:
        return ExternalEnrollmentFileUploadSteps.Received;
      default:
        return 0;
    }
  };

  const getCanceledSteps = (fileStatusTypeBeforeCanceled: number) => {
    if (fileStatusTypeBeforeCanceled === EnrollmentFileStatusType.RECEIVED)
      return ExternalEnrollmentFileUploadSteps.Received;
    else if (fileStatusTypeBeforeCanceled === EnrollmentFileStatusType.PROCESSING)
      return ExternalEnrollmentFileUploadSteps.Processing;
    else if (fileStatusTypeBeforeCanceled === EnrollmentFileStatusType.COMPLETED)
      return ExternalEnrollmentFileUploadSteps.ProcessingComplete;
  };

  const getVerticalStepStatusDetails = (enrollmentFile: EnrollmentFilesDetailDto) => {
    const fileUploadedDate = enrollmentFile.fileUploadedDate!;
    const messageSuccess = 'Successful';
    const messageUnsuccess = 'Unsuccessful';
    const activeStep =
      enrollmentFile.invalidRecordCount! > 0 || enrollmentFile.rowCount! === 0
        ? ExternalEnrollmentFileUploadSteps.FileUpload
        : ExternalEnrollmentFileUploadSteps.Received;
    const verticalSteps = steps.map(step => {
      let message =
        step.id <= activeStep || enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.COMPLETED
          ? 'Successful'
          : 'Unsuccessful';
      let canceledMessage = 'Unsuccessful';
      let processingStep = 2;
      if (
        step.id === ExternalEnrollmentFileUploadSteps.FileUpload ||
        step.id === ExternalEnrollmentFileUploadSteps.Received
      ) {
        if (enrollmentFile.invalidRecordCount! > 0 || enrollmentFile.rowCount! === 0)
          return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(fileUploadedDate)}`;
        else if (
          (enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.CANCELED ||
            enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.Returned) &&
          getProcessingSteps(enrollmentFile?.fileStatusIdBeforeCanceled!) === step.id
        )
          return `${step.value} - ${canceledMessage} - ${toFormattedDateTime(enrollmentFile.fileCanceledDate!)}`;
        else return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(fileUploadedDate)}`;
      } else if (
        (enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.CANCELED ||
          enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.Returned) &&
        getProcessingSteps(enrollmentFile?.fileStatusIdBeforeCanceled!) === step.id
      )
        return `${step.value} - ${canceledMessage} - ${toFormattedDateTime(enrollmentFile.fileCanceledDate!)}`;
      else if (
        getProcessingSteps(enrollmentFile?.fileStatus?.id!) > 0 &&
        enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.COMPLETED
      )
        return `${step.value} - ${message} - ${toFormattedDateTime(enrollmentFile.fileStatusChangedDate!)}`;
      else if (enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.PROCESSING && step.id === processingStep)
        return `${step.value} - ${toFormattedDateTime(enrollmentFile.fileStatusChangedDate!)}`;
      else return step.value;
    });
    return verticalSteps;
  };

  return renderAsyncView(enrollmentApplicationsDetails, details => {
    return (
      <div className="external-enrollment-file">
        {pipe(
          details,
          NEA.fromArray,
          O.map(enrollmentFiles => {
            const enrollments = enrollmentFiles.sort(isFileUploadBefore);
            const latestEnrollmentFile = NEA.head(enrollments);
            const enrollmentFileHistory = NEA.tail(enrollments);
            return (
              <>
                <HorizontalStatusTracker
                  enrollmentFile={latestEnrollmentFile}
                  steps={steps.map(step =>
                    step.id === ExternalEnrollmentFileUploadSteps.ProcessingComplete
                      ? {...step, value: 'Confirmed'}
                      : step
                  )}
                  currentStep={getActiveSteps(
                    latestEnrollmentFile.fileStatus!.id!,
                    latestEnrollmentFile.fileStatusIdBeforeCanceled!
                  )}
                />
                <div className="external-enrollment-file__content-container">
                  <BrokerSpreadsheetFileSummaryAccordion
                    enrollmentFile={latestEnrollmentFile}
                    enrollmentFileHistory={enrollmentFileHistory}
                    fileId={fileId!}
                  />
                  <Accordion className="summary-line-info__expansion-panel error-count">
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      disabled={latestEnrollmentFile.invalidRecordCount === 0}
                    >
                      <div className="summary-line-info__expansion-panel__icon-container file-errors">
                        <i className="icon-container__file-error-icon"></i>
                      </div>
                      <div className="summary-line-info__expansion-panel__file-details">
                        <div className="summary-line-info__expansion-panel__file-details--content">
                          <span className="title">
                            Number of Records with Errors: {latestEnrollmentFile.invalidRecordCount}
                          </span>
                        </div>
                      </div>
                    </AccordionSummary>

                    <AccordionDetails className="summary-line-info__expansion-panel-details">
                      <div className="summary-line-info__expansion-panel-details__actions-container">
                        <Typography
                          className="summary-line-info__expansion-panel-details__actions-container--title"
                          variant="h6"
                        >
                          Error Count By Employee
                        </Typography>

                        <span className="details-button action-mr">
                          {latestEnrollmentFile.invalidRecordCount! > 0 && (
                            <ExportToolbarHeaderIcon
                              title="Export Errors"
                              downloadedFileName="EnrollmentSpreadsheetErrorDetails"
                              extension={FileExtension.CSV}
                              relativeFileLocation={`/enrollmentFiles/${fileId}/details/export/csv`}
                              totalRecordCount={0}
                            />
                          )}
                        </span>
                        <Button
                          className="details-button"
                          onClick={() =>
                            history.push(
                              EnrollmentRouteConstants.BROKER_SPREADSHEET_STATUS_ERROR_REVIEW(latestEnrollmentFile.id!)
                            )
                          }
                          variant="contained"
                          color="primary"
                        >
                          Details
                        </Button>
                      </div>
                      {latestEnrollmentFile.employeeErrorDetails!.map((empData: EmployeeErrorDetail, index: number) => {
                        return (
                          <div className="summary-line-info__expansion-panel-details__history-list" key={index}>
                            <Grid container spacing={4}>
                              <Grid item xs={12} sm={6} md={4}>
                                <span className="title">Last Name: </span>
                                <span className="value">{empData.lastName}</span>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <span className="title">First Name: </span>
                                <span className="value">{empData.firstName}</span>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <span className="title">Error Count: </span>
                                <span className="value">{empData.errorCount}</span>
                              </Grid>
                            </Grid>
                          </div>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                  <div className="section-divider-container">
                    <Typography className="section-divider-container__title" variant="h6">
                      File Status
                    </Typography>
                  </div>
                  <div className="vertical-stepper-info">
                    <Stepper
                      activeStep={getActiveSteps(
                        latestEnrollmentFile.fileStatus!.id!,
                        latestEnrollmentFile.fileStatusIdBeforeCanceled!
                      )}
                      orientation="vertical"
                    >
                      {getVerticalStepStatusDetails(latestEnrollmentFile).map((label, index) => (
                        <Step key={`verticalStepper-${index}`}>
                          <StepLabel
                            StepIconProps={{
                              classes: {
                                active: getActiveStepperClass(
                                  latestEnrollmentFile.invalidRecordCount!,
                                  latestEnrollmentFile.rowCount!,
                                  latestEnrollmentFile.fileStatus!.id as EnrollmentFileStatus,
                                  index,
                                  latestEnrollmentFile.validRecordCount!
                                ),
                                completed: getCompletedStatusClass(
                                  latestEnrollmentFile.invalidRecordCount!,
                                  latestEnrollmentFile.rowCount!,
                                  latestEnrollmentFile.fileStatus!.id as EnrollmentFileStatus,
                                  index,
                                  latestEnrollmentFile.validRecordCount!
                                ),
                              },
                              icon: (
                                <span className="icon">
                                  <i
                                    className={getActiveIcon(
                                      index,
                                      latestEnrollmentFile.validRecordCount!,
                                      latestEnrollmentFile.invalidRecordCount!,
                                      latestEnrollmentFile.rowCount!,
                                      latestEnrollmentFile.fileStatus!.id as EnrollmentFileStatus,
                                      latestEnrollmentFile.fileStatusIdBeforeCanceled!
                                    )}
                                  />
                                </span>
                              ),
                            }}
                          >
                            {label}
                          </StepLabel>
                          <StepContent>
                            {latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.PROCESSING && (
                              <span>
                                {latestEnrollmentFile?.fileProcessReason! === 'Processing'
                                  ? `File processing has begun - ${toFormattedDateTime(
                                      latestEnrollmentFile?.recentActionDate!
                                    )}`
                                  : `${latestEnrollmentFile?.fileProcessReason!} - ${toFormattedDateTime(
                                      latestEnrollmentFile?.recentActionDate!
                                    )} `}
                              </span>
                            )}
                            {latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.CANCELED &&
                              getCanceledSteps(latestEnrollmentFile?.fileStatusIdBeforeCanceled!) === index && (
                                <span>
                                  <CancelIcon className="vertical-cancel__icon" />
                                  {latestEnrollmentFile?.fileCanceledReason!
                                    ? ` Cancelled - ${latestEnrollmentFile?.fileCanceledReason!} - ${toFormattedDateTime(
                                        latestEnrollmentFile?.fileCanceledDate!
                                      )} `
                                    : ` Cancelled - ${toFormattedDateTime(latestEnrollmentFile?.fileCanceledDate!)} `}
                                </span>
                              )}
                          </StepContent>
                          <StepContent>
                            {latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.Returned &&
                              getCanceledSteps(latestEnrollmentFile?.fileStatusIdBeforeCanceled!) === index && (
                                <Accordion className="summary-line-info__expansion-panel">
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <CancelIcon className="vertical-returned__icon" />
                                    {latestEnrollmentFile?.fileCanceledReason!
                                      ? ` Returned - ${latestEnrollmentFile?.fileCanceledReason!} - ${toFormattedDateTime(
                                          latestEnrollmentFile?.fileCanceledDate!
                                        )} `
                                      : ` Returned - ${toFormattedDateTime(latestEnrollmentFile?.fileCanceledDate!)} `}
                                  </AccordionSummary>
                                  <AccordionDetails className="summary-line-info__expansion-panel-details">
                                    <div className="summary-line-info__expansion-panel-details__actions-container">
                                      <Typography
                                        className="summary-line-info__expansion-panel-details__actions-container--title"
                                        variant="h6"
                                      >
                                        Return Comments
                                      </Typography>
                                    </div>
                                    <p>{latestEnrollmentFile?.returnComments!}</p>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  </div>
                </div>
              </>
            );
          }),
          O.getOrElse(() => <div className="">No File Information Available</div>)
        )}
      </div>
    );
  });
};

export default ExternalEnrollmentFileUploadDetails;
